import React, { useMemo, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import { useSaveDailyStatus } from "../../hooks/queries/useAthleteData";
import { buildDailyStatusAnswers } from "../../utils/nutritionStatus";
import { localDateKey } from "../../utils/localDate";

const STEPS = [
  {
    id: "sleep",
    eyebrow: "Paso 1 de 3",
    title: "Calidad de sueno",
  },
  {
    id: "training",
    eyebrow: "Paso 2 de 3",
    title: "Carga de entrenamiento y recuperacion",
  },
  {
    id: "mindset",
    eyebrow: "Paso 3 de 3",
    title: "Como te sientes hoy?",
  },
];

const QUALITY_OPTIONS = [
  { value: "1", label: "1 - Muy mala" },
  { value: "2", label: "2 - Mala" },
  { value: "3", label: "3 - Regular" },
  { value: "4", label: "4 - Buena" },
  { value: "5", label: "5 - Excelente" },
];

const SLEEP_HOURS_OPTIONS = [
  { value: "4", label: "Menos de 4 horas" },
  { value: "4.5", label: "4 - 5 horas" },
  { value: "5.5", label: "5 - 6 horas" },
  { value: "6.5", label: "6 - 7 horas" },
  { value: "7.5", label: "7 - 8 horas" },
  { value: "8.5", label: "8 - 9 horas" },
  { value: "9.5", label: "9 - 10 horas" },
  { value: "10.5", label: "Mas de 10 horas" },
];

const SESSION_DURATION_OPTIONS = [
  { value: "0", label: "No entrene hoy" },
  { value: "15", label: "0 - 30 min" },
  { value: "45", label: "30 - 60 min" },
  { value: "75", label: "60 - 90 min" },
  { value: "105", label: "90 - 120 min" },
  { value: "150", label: "2 - 3 horas" },
  { value: "210", label: "3 - 4 horas" },
  { value: "270", label: "Mas de 4 horas" },
];

const DOMS_OPTIONS = [
  { value: "1", label: "1 - Sin dolor" },
  { value: "2", label: "2 - Muy leve" },
  { value: "3", label: "3 - Leve" },
  { value: "4", label: "4 - Leve moderado" },
  { value: "5", label: "5 - Moderado" },
  { value: "6", label: "6 - Moderado alto" },
  { value: "7", label: "7 - Alto" },
  { value: "8", label: "8 - Muy alto" },
  { value: "9", label: "9 - Severo" },
  { value: "10", label: "10 - Limitante" },
];

const getClosestOptionValue = (value, options) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";

  return options.reduce((closest, option) => {
    const optionValue = Number(option.value);
    const closestValue = Number(closest.value);
    return Math.abs(optionValue - numericValue) <
      Math.abs(closestValue - numericValue)
      ? option
      : closest;
  }, options[0]).value;
};

const SCALE_OPTIONS = Array.from({ length: 10 }, (_, index) => ({
  value: String(index + 1),
  label: `${index + 1}`,
}));

// Prefilled from the last check-in, whichever day it was.
const getInitialFormData = (athleteData) => {
  const latestStatus =
    athleteData?.athlete_tracking?.[0]?.metrics?.dailyStatus?.latest;

  return {
    sleepHours: latestStatus?.sleep?.hours
      ? getClosestOptionValue(latestStatus.sleep.hours, SLEEP_HOURS_OPTIONS)
      : "",
    sleepQuality: latestStatus?.sleep?.quality
      ? String(latestStatus.sleep.quality)
      : "",
    rpe: latestStatus?.training?.rpe ? String(latestStatus.training.rpe) : "",
    sessionDuration: latestStatus?.training?.sessionDuration
      ? getClosestOptionValue(
          latestStatus.training.sessionDuration,
          SESSION_DURATION_OPTIONS,
        )
      : "",
    fatigue: latestStatus?.training?.fatigue
      ? String(latestStatus.training.fatigue)
      : "",
    doms: latestStatus?.training?.doms
      ? String(latestStatus.training.doms)
      : "",
    mood: latestStatus?.mindset?.mood ? String(latestStatus.mindset.mood) : "",
    motivation: latestStatus?.mindset?.motivation
      ? String(latestStatus.mindset.motivation)
      : "",
  };
};

const getProgressWidth = (step) => `${((step + 1) / STEPS.length) * 100}%`;

const SelectField = ({
  label,
  helper,
  name,
  value,
  onChange,
  placeholder,
  options,
  required = false,
  hasError = false,
}) => (
  <label className="block">
    {label && (
      <span className="mb-3 block text-sm font-medium text-[#202636]">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </span>
    )}
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full appearance-none border bg-white px-4 py-3 pr-12 text-sm text-[#111827] outline-none transition-colors ${
          hasError
            ? "border-red-400 bg-red-50 focus:border-red-500"
            : "border-[#cfd4dd] focus:border-[#203c79]"
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={18}
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#3c4354]"
      />
    </div>
    {hasError && (
      <p className="mt-2 text-[11px] font-medium leading-4 text-red-600">
        Campo obligatorio
      </p>
    )}
    {helper && (
      <p className="mt-2 text-[11px] leading-4 text-[#98a2b3]">{helper}</p>
    )}
  </label>
);

const StatusUpdateFlow = ({ athleteData, onBack, onSave }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() =>
    getInitialFormData(athleteData),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const saveDailyStatus = useSaveDailyStatus();

  const currentStepConfig = STEPS[currentStep];

  const isStepValid = useMemo(() => {
    if (currentStep === 0) {
      return Boolean(formData.sleepHours && formData.sleepQuality);
    }

    if (currentStep === 1) {
      return Boolean(
        formData.rpe &&
        formData.sessionDuration &&
        formData.fatigue &&
        formData.doms,
      );
    }

    return Boolean(formData.mood && formData.motivation);
  }, [currentStep, formData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
    setErrorMessage("");
    setShowRequiredErrors(false);
  };

  const handleNext = async () => {
    if (!isStepValid) {
      setShowRequiredErrors(true);
      setErrorMessage("Completa los campos de este paso para continuar.");
      return;
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep((previous) => previous + 1);
      setShowRequiredErrors(false);
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      // The server scores the answers and keeps the day's other sections.
      await saveDailyStatus.mutateAsync({
        date: localDateKey(),
        answers: buildDailyStatusAnswers(formData),
      });
      onSave();
    } catch (saveError) {
      setErrorMessage(
        saveError?.message || "No se pudo guardar el estado. Intenta de nuevo.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#f4f5f8] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl rounded-sm border border-[#d7dce5] bg-white px-4 py-8 shadow-[0_8px_20px_rgba(15,23,42,0.08)] sm:px-7 sm:py-10">
        <div className="mb-10">
          <div className="h-[3px] w-full bg-[#c8d4ed]">
            <div
              className="h-full bg-[#203c79] transition-all duration-300"
              style={{ width: getProgressWidth(currentStep) }}
            />
          </div>
        </div>

        <div className="mx-auto max-w-3xl">
          <p className="text-2xl font-semibold tracking-tight text-[#121826] sm:text-[28px]">
            Actualizar estado
          </p>
          <p className="mt-8 text-base text-[#3c4354]">
            {currentStepConfig.eyebrow}
          </p>
          <h1 className="mt-1 text-3xl font-semibold leading-[1.1] tracking-tight text-[#121826] sm:text-[42px] sm:leading-[1.05]">
            {currentStepConfig.title}
          </h1>

          <div className="mt-8 space-y-7">
            {currentStep === 0 && (
              <>
                <SelectField
                  label="Horas de sueño"
                  name="sleepHours"
                  value={formData.sleepHours}
                  onChange={handleChange}
                  placeholder="Horas de sueno"
                  options={SLEEP_HOURS_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.sleepHours}
                  helper="Elige el rango que mas se acerque a tu descanso real."
                />
                <SelectField
                  label="Calidad de sueño"
                  name="sleepQuality"
                  value={formData.sleepQuality}
                  onChange={handleChange}
                  placeholder="Calidad de sueno"
                  options={QUALITY_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.sleepQuality}
                  helper="1 = Muy mala 3 = Regular 5 = Excelente"
                />
              </>
            )}

            {currentStep === 1 && (
              <>
                <SelectField
                  label="Esfuerzo percibido"
                  name="rpe"
                  value={formData.rpe}
                  onChange={handleChange}
                  placeholder="Esfuerzo percibido (RPE)"
                  options={SCALE_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.rpe}
                  helper="1 = Muy facil 5 = Moderado 8 = Muy duro 10 = Maximo esfuerzo"
                />
                <SelectField
                  label="Duracion de la sesion"
                  name="sessionDuration"
                  value={formData.sessionDuration}
                  onChange={handleChange}
                  placeholder="¿Cuantos minutos duro tu entrenamiento hoy?"
                  options={SESSION_DURATION_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.sessionDuration}
                  helper="Incluye calentamiento y enfriamiento. Si no entrenaste coloca 0."
                />
                <SelectField
                  label="Nivel de fatiga"
                  name="fatigue"
                  value={formData.fatigue}
                  onChange={handleChange}
                  placeholder="Nivel de fatiga"
                  options={SCALE_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.fatigue}
                  helper="1 = Completamente descansado 10 = Agotado total"
                />
                <SelectField
                  label="Dolor muscular (DOMS)"
                  name="doms"
                  value={formData.doms}
                  onChange={handleChange}
                  placeholder="¿Tienes dolor muscular por entrenamientos anteriores?"
                  options={DOMS_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.doms}
                  helper="1 = Sin dolor 10 = Dolor intenso con limitacion de movimiento"
                />
              </>
            )}

            {currentStep === 2 && (
              <>
                <SelectField
                  label="Estado de animo"
                  name="mood"
                  value={formData.mood}
                  onChange={handleChange}
                  placeholder="¿Como describes tu estado emocional hoy?"
                  options={SCALE_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.mood}
                  helper="1 = Muy mal 5 = Neutral 10 = Excelente"
                />
                <SelectField
                  label="Motivacion"
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  placeholder="¿Que tan motivado estas para entrenar hoy?"
                  options={SCALE_OPTIONS}
                  required
                  hasError={showRequiredErrors && !formData.motivation}
                  helper="La falta de motivacion persistente puede ser una senal de sobreentrenamiento."
                />
              </>
            )}
          </div>

          {errorMessage && (
            <div className="mt-6 border border-[#f3c3b7] bg-[#fff4f1] px-4 py-3 text-sm text-[#9f2d17]">
              {errorMessage}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between gap-3 sm:gap-4">
            <button
              onClick={() => {
                if (currentStep === 0) {
                  onBack();
                  return;
                }
                setCurrentStep((previous) => previous - 1);
                setErrorMessage("");
              }}
              className="bg-[#b8c7e6] px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#a8badf] sm:px-6 sm:tracking-[0.35em]"
            >
              Anterior
            </button>

            <button
              onClick={handleNext}
              disabled={isSaving}
              className="inline-flex items-center justify-center gap-2 bg-[#203c79] px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#1a3266] disabled:cursor-not-allowed disabled:bg-[#6e7fa5] sm:min-w-[190px] sm:px-6 sm:tracking-[0.35em]"
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Guardando
                </>
              ) : currentStep === STEPS.length - 1 ? (
                "Guardar estado"
              ) : (
                "Siguiente"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateFlow;
